import React, { useContext, useMemo, useState } from 'react';
import { AuthContext } from '@/App';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  BookOpen,
  Sparkles,
  Search,
  ArrowRight,
  Clock,
  Star,
  TrendingUp,
  Award,
  Users2,
  Church,
  Video,
  Radio,
  Heart,
  Layers,
  FileText,
  Download,
  Eye,
  Bookmark,
  BookmarkX,
  GraduationCap,
  CheckCircle2,
  Circle,
  Lock,
  Compass,
  Flame,
  Coins,
  Target,
  Plus,
  ScrollText,
  PlayCircle
} from 'lucide-react';

// ============================================================
// RESOURCE TOOLKIT — Wave 1 (visual only, no backend)
// Mirrors the design language of Weekly Digest + Creator Growth Engine.
// ============================================================

// Featured resources
const FEATURED = [
  { id: 'f1', title: 'How to Unlock Tier 2 Rewards',     category: 'Rewards',         minutes: 6,  accent: 'amber', icon: Award },
  { id: 'f2', title: 'Content Workflow Starter Guide',   category: 'Content',         minutes: 8,  accent: 'pink',  icon: Layers },
  { id: 'f3', title: 'Affiliate Growth Playbook',         category: 'Monetization',    minutes: 12, accent: 'green', icon: Users2 },
  { id: 'f4', title: 'Church Partner Certification Guide',category: 'Certification',  minutes: 10, accent: 'rose',  icon: Church },
  { id: 'f5', title: 'Creator Momentum Blueprint',        category: 'Creator Growth',  minutes: 9,  accent: 'cyan',  icon: Compass }
];

// Browse-by-category data
const CATEGORIES = [
  {
    id: 'creator-growth',
    title: 'Creator Growth',
    icon: TrendingUp,
    accent: 'cyan',
    resources: [
      { id: 'cg1', title: 'Consistency Fundamentals',       minutes: 5 },
      { id: 'cg2', title: 'Momentum Building',              minutes: 7 },
      { id: 'cg3', title: 'Audience Growth',                minutes: 9 },
      { id: 'cg4', title: 'Creator Growth Engine Guide',    minutes: 11 }
    ]
  },
  {
    id: 'content',
    title: 'Content',
    icon: Video,
    accent: 'pink',
    resources: [
      { id: 'c1', title: 'Content Planning',                minutes: 6 },
      { id: 'c2', title: 'Short Form Templates',            minutes: 5 },
      { id: 'c3', title: 'Live Streaming Guides',           minutes: 9 },
      { id: 'c4', title: 'Workflow SOPs',                   minutes: 12 }
    ]
  },
  {
    id: 'rewards',
    title: 'Rewards',
    icon: Award,
    accent: 'amber',
    resources: [
      { id: 'r1', title: 'Badge System Guide',              minutes: 5 },
      { id: 'r2', title: 'Bonus Qualification Guide',       minutes: 6 },
      { id: 'r3', title: 'Plaque Qualification Guide',      minutes: 7 }
    ]
  },
  {
    id: 'monetization',
    title: 'Monetization',
    icon: Coins,
    accent: 'green',
    resources: [
      { id: 'm1', title: 'Affiliate Program Guide',         minutes: 8 },
      { id: 'm2', title: 'Church Partner Guide',            minutes: 10 },
      { id: 'm3', title: 'Revenue Strategy Resources',      minutes: 9 }
    ]
  },
  {
    id: 'community',
    title: 'Community',
    icon: Heart,
    accent: 'rose',
    resources: [
      { id: 'co1', title: 'Networking Resources',           minutes: 4 },
      { id: 'co2', title: 'Collaboration Templates',        minutes: 6 },
      { id: 'co3', title: 'Community Best Practices',       minutes: 7 }
    ]
  }
];

// Personalized recommendations
const RECOMMENDED = [
  { id: 'p1', title: "You're 1 workflow away from Tier 2", reason: 'Read the unlock guide to finish your current pipeline.', cta: 'Open Guide', accent: 'amber', icon: Award },
  { id: 'p2', title: 'Complete Affiliate Setup',           reason: 'Set up your referral link to start earning this week.', cta: 'Open Setup',  accent: 'green', icon: Users2 },
  { id: 'p3', title: 'Improve Engagement Rate',            reason: 'Response rate dropped 12% — try our 3-step engagement playbook.', cta: 'Open Playbook', accent: 'cyan', icon: Heart },
  { id: 'p4', title: 'Recover Posting Streak',             reason: 'You missed 2 uploads. Use the Streak Protection SOP.', cta: 'Open SOP', accent: 'red',   icon: Flame },
  { id: 'p5', title: 'Schedule Your Next Live',            reason: 'Live engagement is rising — book your next session in 5 mins.', cta: 'Open Checklist', accent: 'pink', icon: Radio }
];

// Templates
const TEMPLATES = [
  { id: 't1', name: 'Weekly Content Planner',     desc: 'Map 7 days of content across all your platforms in one view.',         format: 'PDF • Notion',  accent: 'pink',  icon: FileText },
  { id: 't2', name: 'Live Stream Checklist',      desc: 'Pre-live tech, audience prompts, and post-live recap workflow.',       format: 'PDF',           accent: 'cyan',  icon: Radio },
  { id: 't3', name: 'Affiliate Outreach Script',   desc: 'Plug-and-play DM + email scripts for converting referrals.',           format: 'DOCX',         accent: 'green', icon: Users2 },
  { id: 't4', name: 'Church Introduction Script',  desc: 'First-meeting script for introducing the church partner program.',     format: 'PDF',          accent: 'rose',  icon: Church },
  { id: 't5', name: 'Audience Follow-Up Template', desc: '5-touchpoint follow-up sequence to re-warm cold audience members.',    format: 'DOCX',         accent: 'amber', icon: Heart },
  { id: 't6', name: 'Creator Goal Tracker',        desc: 'Weekly + quarterly goal tracking aligned to the Growth Engine.',       format: 'Sheets • PDF', accent: 'orange',icon: Target }
];

// Certification modules
const CERT_MODULES = [
  { id: 'm1', title: 'Sales Training Module 1',           status: 'done',     desc: 'Foundations — value proposition & first meeting.', icon: GraduationCap },
  { id: 'm2', title: 'Sales Training Module 2',           status: 'done',     desc: 'Objection handling & decision-maker mapping.',     icon: GraduationCap },
  { id: 'm3', title: 'Sales Training Module 3',           status: 'done',     desc: 'Closing technique & onboarding handoff.',           icon: GraduationCap },
  { id: 'm4', title: 'Sales Training Module 4',           status: 'done',     desc: 'Renewal cadence & long-term partner success.',     icon: GraduationCap },
  { id: 'm5', title: 'Sales Training Module 5',           status: 'done',     desc: 'Territory planning & pipeline hygiene.',            icon: GraduationCap },
  { id: 'm6', title: 'Sales Training Module 6',           status: 'done',     desc: 'Compliance & contract walkthrough.',                icon: GraduationCap },
  { id: 'm7', title: 'Certification Exam Guide',          status: 'progress', desc: 'Study guide + practice scenarios for the exam.',    icon: ScrollText },
  { id: 'm8', title: 'Church Partner Handbook',           status: 'locked',   desc: 'Reference handbook unlocked after certification.',   icon: BookOpen }
];

// Recently added
const RECENT = [
  { id: 'rn1', title: 'Creator Growth Engine Update',      added: 'Feb 14, 2026', category: 'Creator Growth',  accent: 'cyan' },
  { id: 'rn2', title: 'New Affiliate Scripts',             added: 'Feb 12, 2026', category: 'Monetization',    accent: 'green' },
  { id: 'rn3', title: 'Updated Church Partner Handbook',   added: 'Feb 09, 2026', category: 'Certification',   accent: 'rose' },
  { id: 'rn4', title: 'New Live Stream Checklist',         added: 'Feb 06, 2026', category: 'Content',         accent: 'pink' }
];

// Initial saved
const INITIAL_SAVED = [
  { id: 's1', title: 'Affiliate Growth Playbook',  category: 'Monetization',   accent: 'green' },
  { id: 's2', title: 'Momentum Blueprint',          category: 'Creator Growth', accent: 'cyan' },
  { id: 's3', title: 'Creator Goal Tracker',        category: 'Template',       accent: 'amber' }
];

// Reusable
const SourceChip = ({ source }) => (
  <span className="inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-[0.14em] text-[#555E6E]">
    <span className="w-1 h-1 rounded-full bg-[#3A4150]" />
    {source}
  </span>
);

const SectionHeader = ({ icon: Icon, accent = 'cyan', title, subtitle, source, right }) => (
  <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
    <div>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 text-${accent}-400`} />
        <h2 className="text-base font-semibold text-white">{title}</h2>
      </div>
      {subtitle && <p className="text-[11px] text-[#8A93A6] mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-3">
      {source && <SourceChip source={source} />}
      {right}
    </div>
  </div>
);

// ============================================================
// MAIN
// ============================================================
export default function ResourceToolkit() {
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_internal_admin;
  const [query, setQuery] = useState('');
  const [saved, setSaved] = useState(INITIAL_SAVED);

  const featuredFiltered = useMemo(() => {
    if (!query) return FEATURED;
    const q = query.toLowerCase();
    return FEATURED.filter((f) => f.title.toLowerCase().includes(q) || f.category.toLowerCase().includes(q));
  }, [query]);

  const removeBookmark = (id) => {
    setSaved((s) => s.filter((x) => x.id !== id));
    toast.success('Removed from saved resources');
  };

  const certDone = CERT_MODULES.filter((m) => m.status === 'done').length;
  const certTotal = CERT_MODULES.length;
  const certPct = Math.round((certDone / certTotal) * 100);

  return (
    <div className="container mx-auto p-6 max-w-6xl" data-testid="resource-toolkit-page">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <BookOpen className="h-6 w-6 text-orange-400" />
            <h1 className="text-3xl font-bold text-white">Resource Toolkit</h1>
            <Badge variant="outline" className="text-[10px] border-cyan-500/30 text-cyan-300 bg-cyan-500/10 uppercase tracking-wider font-semibold">
              Creator Education
            </Badge>
            <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-300 bg-amber-500/10 uppercase tracking-wider font-semibold">
              Template Library
            </Badge>
            {isAdmin && (
              <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
                Admin Preview
              </Badge>
            )}
          </div>
          <p className="text-[#8A93A6] max-w-2xl">
            Creator education, templates, SOPs, and implementation resources.
          </p>
        </div>

        <div className="relative w-full md:w-[300px]">
          <Search className="h-4 w-4 text-[#8A93A6] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search resources, templates, SOPs…"
            className="pl-9 h-9 bg-[#1C1F26] border-white/10 text-white text-xs"
            data-testid="resource-search-input"
          />
        </div>
      </div>

      {/* SECTION 1 — Featured Resources */}
      <section className="mb-7" data-testid="section-featured">
        <SectionHeader
          icon={Star}
          accent="amber"
          title="Featured Resources"
          subtitle="Hand-picked guides every creator should read first."
          source="Editor's Picks"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {featuredFiltered.map((f) => {
            const Icon = f.icon;
            return (
              <Card key={f.id} className="glass border-white/5 hover:border-white/10 transition-colors flex flex-col" data-testid={`featured-${f.id}`}>
                <CardContent className="p-3.5 flex flex-col h-full">
                  <div className={`w-9 h-9 rounded-md bg-${f.accent}-500/10 border border-${f.accent}-500/20 flex items-center justify-center mb-3`}>
                    <Icon className={`h-4 w-4 text-${f.accent}-400`} />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] font-medium mb-1">{f.category}</p>
                  <p className="text-[13px] font-semibold text-white leading-tight mb-2 flex-1">{f.title}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#8A93A6]">
                      <Clock className="h-3 w-3" />
                      {f.minutes} min read
                    </span>
                    <button
                      type="button"
                      onClick={() => toast.info(`Opening "${f.title}"`)}
                      className={`text-[10px] uppercase tracking-wider text-${f.accent}-300 hover:text-${f.accent}-200 inline-flex items-center gap-1 font-semibold`}
                      data-testid={`open-featured-${f.id}`}
                    >
                      Open
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {featuredFiltered.length === 0 && (
            <div className="col-span-full rounded-md border border-dashed border-white/10 bg-white/[0.015] px-4 py-6 text-center">
              <p className="text-xs text-[#8A93A6]">No resources match "{query}". Try a different search.</p>
            </div>
          )}
        </div>
      </section>

      {/* SECTION 2 — Browse by Category */}
      <section className="mb-7" data-testid="section-categories">
        <SectionHeader
          icon={Compass}
          accent="cyan"
          title="Browse by Category"
          subtitle="Every creator system has a dedicated knowledge library."
          source="Library"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <Card key={cat.id} className="glass border-white/5 hover:border-white/10 transition-colors" data-testid={`category-${cat.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-md bg-${cat.accent}-500/10 border border-${cat.accent}-500/20 flex items-center justify-center`}>
                        <Icon className={`h-3.5 w-3.5 text-${cat.accent}-400`} />
                      </div>
                      <p className="text-sm font-semibold text-white">{cat.title}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-white/10 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider">
                      {cat.resources.length} resources
                    </Badge>
                  </div>
                  <div className="space-y-1.5">
                    {cat.resources.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => toast.info(`Opening "${r.title}"`)}
                        className="w-full flex items-center justify-between gap-2 rounded-md border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10 px-3 py-2 text-left transition-colors"
                        data-testid={`category-resource-${r.id}`}
                      >
                        <span className="text-xs text-white truncate">{r.title}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] text-[#8A93A6] shrink-0">
                          <Clock className="h-3 w-3" />
                          {r.minutes}m
                        </span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* SECTION 3 — Recommended For You */}
      <section className="mb-7" data-testid="section-recommended">
        <SectionHeader
          icon={Sparkles}
          accent="pink"
          title="Recommended For You"
          subtitle="Personalized to your current growth state and rewards progress."
          source="Growth Engine"
          right={
            <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
              Personalized
            </Badge>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {RECOMMENDED.map((rec) => {
            const Icon = rec.icon;
            return (
              <Card key={rec.id} className="glass border-white/5 hover:border-white/10 transition-colors" data-testid={`rec-${rec.id}`}>
                <CardContent className="p-3.5">
                  <div className="flex items-start gap-3 mb-2">
                    <div className={`w-8 h-8 rounded-md bg-${rec.accent}-500/10 border border-${rec.accent}-500/20 flex items-center justify-center shrink-0`}>
                      <Icon className={`h-3.5 w-3.5 text-${rec.accent}-400`} />
                    </div>
                    <p className="text-[13px] font-semibold text-white leading-tight">{rec.title}</p>
                  </div>
                  <p className="text-[11px] text-[#8A93A6] leading-relaxed mb-3">{rec.reason}</p>
                  <button
                    type="button"
                    onClick={() => toast.info(`Opening "${rec.title}"`)}
                    className={`text-[11px] font-semibold uppercase tracking-wider text-${rec.accent}-300 hover:text-${rec.accent}-200 inline-flex items-center gap-1.5`}
                    data-testid={`open-rec-${rec.id}`}
                  >
                    {rec.cta}
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* SECTION 4 — Downloadable Templates */}
      <section className="mb-7" data-testid="section-templates">
        <SectionHeader
          icon={FileText}
          accent="amber"
          title="Downloadable Templates"
          subtitle="Plug-and-play assets you can use today."
          source="Template Library"
        />
        <Card className="glass border-white/5">
          <CardContent className="p-0">
            <div className="divide-y divide-white/[0.04]">
              {TEMPLATES.map((t) => {
                const Icon = t.icon;
                return (
                  <div
                    key={t.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors"
                    data-testid={`template-${t.id}`}
                  >
                    <div className={`w-9 h-9 rounded-md bg-${t.accent}-500/10 border border-${t.accent}-500/20 flex items-center justify-center shrink-0`}>
                      <Icon className={`h-4 w-4 text-${t.accent}-400`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-white truncate">{t.name}</p>
                      <p className="text-[11px] text-[#8A93A6] truncate">{t.desc}</p>
                    </div>
                    <Badge variant="outline" className="hidden md:inline-flex text-[10px] border-white/10 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider shrink-0">
                      {t.format}
                    </Badge>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-[11px]"
                        onClick={() => toast.info(`Previewing "${t.name}"`)}
                        data-testid={`preview-${t.id}`}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1.5" />
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        className="h-8 text-[11px] bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25"
                        onClick={() => toast.success(`"${t.name}" download started`)}
                        data-testid={`download-${t.id}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Download
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 5 — Certification Resources */}
      <section className="mb-7" data-testid="section-certification">
        <SectionHeader
          icon={GraduationCap}
          accent="rose"
          title="Church Partner Certification"
          subtitle="Complete the training path to become a Certified Church Partner Rep."
          source="Church Partner"
          right={
            <Link to="/app/account/church-partner">
              <Button variant="outline" size="sm" data-testid="open-church-partner-btn">
                Open Program
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </Link>
          }
        />
        <Card className="glass border-white/5">
          <CardContent className="p-4">
            {/* Progress bar */}
            <div className="rounded-md border border-rose-500/20 bg-rose-500/[0.04] px-4 py-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#8A93A6] font-medium">Training Progress</p>
                  <p className="text-sm font-semibold text-white">
                    {certDone} of {certTotal} Modules Complete
                  </p>
                </div>
                <span className="text-2xl font-bold text-rose-200 tabular-nums">{certPct}%</span>
              </div>
              <Progress value={certPct} className="h-1.5 bg-white/[0.04] [&>div]:bg-rose-400" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {CERT_MODULES.map((m) => {
                const Icon = m.icon;
                const done = m.status === 'done';
                const progress = m.status === 'progress';
                const locked = m.status === 'locked';
                return (
                  <div
                    key={m.id}
                    className={`flex items-start gap-3 rounded-md border px-3 py-2.5 transition-colors ${
                      done
                        ? 'border-green-500/20 bg-green-500/[0.04]'
                        : progress
                        ? 'border-amber-500/25 bg-amber-500/[0.05]'
                        : 'border-white/5 bg-white/[0.02] opacity-70'
                    }`}
                    data-testid={`cert-module-${m.id}`}
                  >
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                      done ? 'bg-green-500/15 border border-green-500/25' :
                      progress ? 'bg-amber-500/15 border border-amber-500/25' :
                      'bg-white/[0.04] border border-white/10'
                    }`}>
                      {done ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
                      ) : locked ? (
                        <Lock className="h-3.5 w-3.5 text-[#8A93A6]" />
                      ) : (
                        <Icon className="h-3.5 w-3.5 text-amber-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className="text-xs font-semibold text-white truncate">{m.title}</p>
                        <Badge
                          variant="outline"
                          className={`text-[9px] uppercase tracking-wider px-1.5 py-0 shrink-0 ${
                            done ? 'border-green-500/30 text-green-300 bg-green-500/10' :
                            progress ? 'border-amber-500/30 text-amber-300 bg-amber-500/10' :
                            'border-white/10 text-[#8A93A6] bg-white/[0.03]'
                          }`}
                        >
                          {done ? 'Done' : progress ? 'In Progress' : 'Locked'}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-[#8A93A6] truncate">{m.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* SECTION 6 + 7 — Recently Added + Saved Resources */}
      <section className="mb-7 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="section-recent-saved">
        {/* Recently Added */}
        <Card className="glass border-white/5" data-testid="recently-added-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-cyan-400" />
                <p className="text-sm font-semibold text-white">Recently Added</p>
              </div>
              <SourceChip source="Library updates" />
            </div>
            <div className="space-y-1.5">
              {RECENT.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-3 rounded-md border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10 px-3 py-2 transition-colors"
                  data-testid={`recent-${r.id}`}
                >
                  <div className={`w-7 h-7 rounded-md bg-${r.accent}-500/10 border border-${r.accent}-500/20 flex items-center justify-center shrink-0`}>
                    <Sparkles className={`h-3.5 w-3.5 text-${r.accent}-400`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{r.title}</p>
                    <p className="text-[10px] text-[#8A93A6]">
                      <span className={`text-${r.accent}-300`}>{r.category}</span>
                      <span className="mx-1.5 text-[#3A4150]">•</span>
                      {r.added}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toast.info(`Opening "${r.title}"`)}
                    className="text-[10px] uppercase tracking-wider text-[#8A93A6] hover:text-white inline-flex items-center gap-1 shrink-0"
                  >
                    Open
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Saved Resources */}
        <Card className="glass border-white/5" data-testid="saved-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bookmark className="h-4 w-4 text-amber-400" />
                <p className="text-sm font-semibold text-white">Saved Resources</p>
              </div>
              <SourceChip source="Your bookmarks" />
            </div>
            {saved.length === 0 ? (
              <div className="rounded-md border border-dashed border-white/10 bg-white/[0.015] px-4 py-6 flex flex-col items-center text-center">
                <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-2">
                  <Bookmark className="h-4 w-4 text-amber-400" />
                </div>
                <p className="text-xs font-semibold text-white">Nothing saved yet</p>
                <p className="text-[11px] text-[#8A93A6] mt-1 max-w-[280px] leading-relaxed">
                  Bookmark any resource to keep it one click away from your toolkit.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {saved.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center gap-3 rounded-md border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10 px-3 py-2 transition-colors"
                    data-testid={`saved-${s.id}`}
                  >
                    <div className={`w-7 h-7 rounded-md bg-${s.accent}-500/10 border border-${s.accent}-500/20 flex items-center justify-center shrink-0`}>
                      <Bookmark className={`h-3.5 w-3.5 text-${s.accent}-400`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-white truncate">{s.title}</p>
                      <p className={`text-[10px] text-${s.accent}-300`}>{s.category}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => toast.info(`Opening "${s.title}"`)}
                        className="text-[10px] uppercase tracking-wider text-[#8A93A6] hover:text-white inline-flex items-center gap-1"
                        data-testid={`open-saved-${s.id}`}
                      >
                        Open
                        <ArrowRight className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeBookmark(s.id)}
                        className="text-[10px] uppercase tracking-wider text-[#8A93A6] hover:text-red-300 inline-flex items-center gap-1"
                        data-testid={`remove-saved-${s.id}`}
                        aria-label={`Remove bookmark ${s.title}`}
                      >
                        <BookmarkX className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Footer hint */}
      <div className="rounded-md border border-white/5 bg-white/[0.02] px-4 py-3 flex items-start gap-3" data-testid="toolkit-footer-note">
        <PlayCircle className="h-4 w-4 text-cyan-400 mt-0.5 shrink-0" />
        <p className="text-[11px] text-[#8A93A6] leading-relaxed">
          This is the central educational hub for the Creator Growth Engine, Rewards, Affiliates, Church Partner, Content Creation, and Community systems. New resources are added weekly — bookmark anything you want to revisit.
        </p>
      </div>
    </div>
  );
}
